import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, GripVertical, Check, X, 
  RefreshCw, Loader2, Link as LinkIcon, Calendar, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminHeroSlider = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hero-slider/messages?include_inactive=true`);
      if (res.ok) {
        setMessages(await res.json());
      }
    } catch (error) {
      toast.error('Failed to load messages');
    }
    setLoading(false);
  };

  useEffect(() => { fetchMessages(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/hero-slider/messages/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Message deleted');
        fetchMessages();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleStatus = async (item) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    
    try {
      const res = await fetch(`${API_URL}/api/hero-slider/messages/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success(newStatus === 'active' ? 'Activated' : 'Deactivated');
        fetchMessages();
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newMessages = [...messages];
    const draggedMessage = newMessages[draggedItem];
    newMessages.splice(draggedItem, 1);
    newMessages.splice(index, 0, draggedMessage);
    
    setMessages(newMessages);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    if (draggedItem === null) return;
    
    try {
      const res = await fetch(`${API_URL}/api/hero-slider/messages/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_ids: messages.map(m => m.id) })
      });
      
      if (res.ok) {
        toast.success('Order saved');
      }
    } catch (error) {
      toast.error('Failed to save order');
    }
    
    setDraggedItem(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-semibold text-ivory-800">HERO Slider</h2>
          <p className="text-ivory-500 font-body">Manage rotating messages in the header</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchMessages} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
          <Button onClick={() => { setEditingItem(null); setShowModal(true); }} className="btn-luxury-primary gap-2">
            <Plus className="w-4 h-4" /> Add Message
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-700">
          <strong>Tip:</strong> Drag messages to reorder. Only active messages within their date range will display on the website.
        </p>
      </div>

      {/* Messages List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-gold-600 animate-spin" />
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 bg-ivory-100 rounded-xl border border-ivory-200">
          <p className="text-ivory-500">No messages yet. Add your first message!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map((msg, index) => (
            <div
              key={msg.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`bg-ivory-100 rounded-xl border border-ivory-200 p-4 flex items-center gap-4 cursor-move transition-all ${
                draggedItem === index ? 'opacity-50 border-gold-400' : ''
              } ${msg.status !== 'active' ? 'opacity-60' : ''}`}
              data-testid={`slider-message-${msg.id}`}
            >
              {/* Drag Handle */}
              <div className="text-ivory-400 hover:text-ivory-600">
                <GripVertical className="w-5 h-5" />
              </div>

              {/* Order Number */}
              <div className="w-8 h-8 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center font-bold text-sm">
                {index + 1}
              </div>

              {/* Message Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`font-semibold text-ivory-800 truncate ${
                    msg.emphasis === 'extra-bold' ? 'font-black' : msg.emphasis === 'bold' ? 'font-bold' : 'font-medium'
                  }`}>
                    {msg.text}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    msg.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-ivory-200 text-ivory-500'
                  }`}>
                    {msg.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-ivory-500">
                  {msg.link_url && (
                    <span className="flex items-center gap-1">
                      <LinkIcon className="w-3 h-3" /> {msg.link_url.substring(0, 30)}...
                    </span>
                  )}
                  {(msg.start_date || msg.end_date) && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {msg.start_date ? new Date(msg.start_date).toLocaleDateString() : '∞'} - {msg.end_date ? new Date(msg.end_date).toLocaleDateString() : '∞'}
                    </span>
                  )}
                  <span className="capitalize bg-ivory-200 px-1.5 py-0.5 rounded">{msg.emphasis}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleStatus(msg)}
                  className={msg.status === 'active' ? 'text-green-600' : 'text-ivory-400'}
                  title={msg.status === 'active' ? 'Deactivate' : 'Activate'}
                >
                  {msg.status === 'active' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setEditingItem(msg); setShowModal(true); }}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(msg.id)} className="text-red-500">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <MessageModal
          item={editingItem}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchMessages(); }}
        />
      )}
    </div>
  );
};

// ============ Message Modal ============
const MessageModal = ({ item, onClose, onSave }) => {
  const isEditing = !!item;
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    text: item?.text || '',
    status: item?.status || 'active',
    emphasis: item?.emphasis || 'bold',
    link_url: item?.link_url || '',
    link_new_tab: item?.link_new_tab ?? true,
    start_date: item?.start_date ? item.start_date.split('T')[0] : '',
    end_date: item?.end_date ? item.end_date.split('T')[0] : '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const endpoint = isEditing
      ? `${API_URL}/api/hero-slider/messages/${item.id}`
      : `${API_URL}/api/hero-slider/messages`;
    
    const body = {
      text: form.text,
      status: form.status,
      emphasis: form.emphasis,
      link_url: form.link_url || null,
      link_new_tab: form.link_new_tab,
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
    };

    try {
      const res = await fetch(endpoint, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        toast.success(isEditing ? 'Updated' : 'Created');
        onSave();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Error saving');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-ivory-50 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-ivory-200 flex justify-between items-center">
          <h3 className="text-xl font-heading font-semibold text-ivory-800">
            {isEditing ? 'Edit Message' : 'Add Message'}
          </h3>
          <button onClick={onClose} className="text-ivory-400 hover:text-ivory-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Message Text *</Label>
            <Input
              value={form.text}
              onChange={(e) => setForm({...form, text: e.target.value.toUpperCase()})}
              placeholder="FREE SHIPPING ON SELECT PRODUCTS"
              required
              maxLength={200}
              className="font-semibold"
              data-testid="message-text-input"
            />
            <p className="text-xs text-ivory-400 mt-1">{form.text.length}/200 characters</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <select
                value={form.status}
                onChange={(e) => setForm({...form, status: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-ivory-300 bg-ivory-50"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <Label>Emphasis</Label>
              <select
                value={form.emphasis}
                onChange={(e) => setForm({...form, emphasis: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-ivory-300 bg-ivory-50"
              >
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
                <option value="extra-bold">Extra Bold</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Link URL (optional)</Label>
            <Input
              value={form.link_url}
              onChange={(e) => setForm({...form, link_url: e.target.value})}
              placeholder="https://..."
              type="url"
            />
            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={form.link_new_tab}
                onChange={(e) => setForm({...form, link_new_tab: e.target.checked})}
                className="rounded border-ivory-300"
              />
              <span className="text-sm text-ivory-600">Open in new tab</span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date (optional)</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({...form, start_date: e.target.value})}
              />
            </div>
            <div>
              <Label>End Date (optional)</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({...form, end_date: e.target.value})}
              />
            </div>
          </div>

          {/* Preview */}
          <div className="p-4 bg-gradient-to-r from-gold-700 via-gold-600 to-gold-500 rounded-lg">
            <p className="text-center text-white text-sm font-body">Preview:</p>
            <p className={`text-center text-white mt-2 ${
              form.emphasis === 'extra-bold' ? 'font-black text-lg' : form.emphasis === 'bold' ? 'font-bold' : 'font-medium'
            }`} style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>
              {form.text || 'YOUR MESSAGE HERE'}
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-ivory-200">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" className="btn-luxury-primary flex-1" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminHeroSlider;
